import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api/axios';
import { T, useContent } from '../../content/ContentProvider';

/**
 * Mandatory password-change screen shown when passwordChangeRequired=true.
 * The backend clears the flag on POST /api/user/profile/change-password.
 * Built on hitrail-ds.css's .gate — the pre-app gate screen the foundation
 * defines for exactly this "before you can use the app" use case.
 */
const ChangePassword = () => {
  const { t } = useContent();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPassword !== form.confirmPassword) {
      setError(t('changePassword.error.mismatch'));
      return;
    }
    if (form.newPassword.length < 8) {
      setError(t('changePassword.error.tooShort'));
      return;
    }
    setLoading(true);
    try {
      await apiClient.post('/api/user/profile/change-password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      // Reload user from /me so passwordChangeRequired is refreshed in context
      await apiClient.get('/api/auth/me');
      // Navigate to home — ProtectedRoute will re-evaluate
      navigate('/', { replace: true });
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || t('changePassword.error.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="gate">
      <div className="gate__card">
        <h2 className="gate__title" style={{ textAlign: 'left' }}>{t('changePassword.heading')}</h2>
        <p className="gate__sub" style={{ textAlign: 'left', marginBottom: 'var(--sp-8)' }}>
          <T k="changePassword.sub" />
        </p>

        {error && (
          <div className="status-msg status-msg--err" role="alert" aria-live="polite" style={{ marginBottom: 'var(--sp-7)', textAlign: 'left' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-7)', textAlign: 'left' }}>
          <div className="field">
            <label className="field__label" htmlFor="cp-current">{t('changePassword.field.current')}</label>
            <input
              id="cp-current"
              type="password"
              name="currentPassword"
              className="field__input field__input--sans"
              value={form.currentPassword}
              onChange={handleChange}
              required
              autoFocus
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="cp-new">{t('changePassword.field.new')}</label>
            <input
              id="cp-new"
              type="password"
              name="newPassword"
              className="field__input field__input--sans"
              value={form.newPassword}
              onChange={handleChange}
              required
              minLength={8}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="cp-confirm">{t('changePassword.field.confirm')}</label>
            <input
              id="cp-confirm"
              type="password"
              name="confirmPassword"
              className="field__input field__input--sans"
              value={form.confirmPassword}
              onChange={handleChange}
              required
            />
          </div>

          <button type="submit" className="btn btn--primary btn--block" disabled={loading}>
            {loading ? t('changePassword.submitBusy') : t('changePassword.submit')}
          </button>
        </form>

        <button
          type="button"
          className="btn btn--ghost btn--block"
          onClick={logout}
          style={{ marginTop: 'var(--sp-6)' }}
        >
          {t('changePassword.logout')}
        </button>
      </div>
    </div>
  );
};

export default ChangePassword;
